// 🔥 PHASE 3: Système de retry intelligent avec exponential backoff
// Retry automatique pour les erreurs de timeout et réseau

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

interface RetryOptions {
  maxRetries?: number;
  onRetry?: (attempt: number, error: any) => void;
}

/**
 * Détecte si une erreur est "retryable" (timeout, réseau, etc.)
 */
function isRetryableError(error: any): boolean {
  const errorMessage = error?.message?.toLowerCase() || '';
  const errorName = error?.name?.toLowerCase() || '';
  
  return (
    errorMessage.includes('timeout') ||
    errorMessage.includes('network') ||
    errorMessage.includes('fetch') ||
    errorMessage.includes('signal timed out') ||
    errorName.includes('aborterror') ||
    errorName.includes('timeouterror')
  );
}

/**
 * Wrapper pour exécuter une opération Supabase avec retry automatique
 * 
 * @example
 * const data = await supabaseWithRetry(
 *   () => supabase.from('club_members').select('*')
 * );
 */
export async function supabaseWithRetry<T>(
  operation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const { maxRetries = 3, onRetry } = options;
  
  let lastError: any;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      // Tentative d'exécution
      const result = await operation();
      
      // Succès - retourner le résultat
      if (attempt > 0) {
        console.log(`✅ Retry succeeded on attempt ${attempt + 1}`);
      }
      return result;
      
    } catch (error: any) {
      lastError = error;
      
      // Si c'est la dernière tentative OU si l'erreur n'est pas retryable
      if (attempt === maxRetries - 1 || !isRetryableError(error)) {
        console.error(`❌ Operation failed (attempt ${attempt + 1}/${maxRetries}):`, error);
        throw error;
      }
      
      // Calculer le délai avec exponential backoff : 1s, 2s, 4s
      const delayMs = Math.pow(2, attempt) * 1000;
      
      console.warn(
        `⚠️ Retry attempt ${attempt + 1}/${maxRetries} after ${delayMs}ms`,
        { error: error.message }
      );
      
      // Notifier de la tentative de retry
      if (onRetry) {
        onRetry(attempt + 1, error);
      }
      
      // Attendre avant de réessayer
      await delay(delayMs);
    }
  }
  
  // Ne devrait jamais arriver ici, mais au cas où
  throw lastError;
}

/**
 * Wrapper spécialisé pour les requêtes Supabase .select()
 * 
 * @example
 * const { data, error } = await supabaseSelectWithRetry(
 *   () => supabase.from('club_members').select('*').eq('user_id', userId)
 * );
 */
export async function supabaseSelectWithRetry<T = any>(
  queryBuilder: () => any,
  options: RetryOptions = {}
) {
  return supabaseWithRetry(
    async () => {
      const result = await queryBuilder();
      
      // Si Supabase retourne une erreur dans { data, error }
      if (result.error) {
        throw result.error;
      }
      
      return result;
    },
    options
  );
}
